/**
 * ★ A VOZ ACOMPANHA O TURNO — [spec-008 T063], US-1/US-4. Décimo irmão de
 * `transporte-tempo-real.js`, extraído do `-delegacao.js` quando ele bateu no
 * teto de 500 linhas pela segunda vez (a primeira foi a [T040b], que produziu
 * o `-remissoes.js`).
 *
 * Aqui não há `RTCPeerConnection`, nem `apiVoz`, nem DOM: entra "uma consulta
 * começou e o consultor está esperando", saem `session.commentary.append`
 * espaçados no tempo, e o modelo verbaliza cada um.
 *
 * ## ★ O defeito que este módulo conserta, e como ele foi MEDIDO
 *
 * O owner mediu, usando: num turno longo, **a voz só falou no final**. A
 * resposta apareceu em texto e o áudio veio de uma vez, como monólogo — e a
 * promessa da spec é diálogo.
 *
 * Havia três suspeitos. O fio diz qual era:
 *
 * | # | suspeito | veredito, com a evidência |
 * |---|---|---|
 * | 1 | *"o modelo não obedece a instrução da [T048]"* | **obedece — uma vez, e só.** No navegador real da [T050] ele anuncia `" Certo, já tô consultando essa informação pra você."` de 12,69 s a 14,83 s, logo após o `function_call` |
 * | 2 | **★ nada é empurrado DURANTE** | **É ESTE.** Depois do anúncio, **31,1 s de silêncio absoluto** (14,83 s → 45,90 s) com a delegação pendente: zero `session.output_transcript.delta`, e os únicos eventos da janela são os três `session.usage.updated` do ledger — ou seja, o canal estava vivo e a IA, calada |
 * | 3 | *"a tela segura a fala"* | **não.** O `<audio autoplay>` do composer é montado SEMPRE (nunca sob `v-if`) e recebe o `MediaStream` uma vez, no `ontrack`; ele toca uma trilha CONTÍNUA. Não existe porta por utterance — e a prova empírica é que a fala do fim foi ouvida, pelo mesmo elemento |
 *
 * Crus: `~/aawz-tmp/spec008-t050/eventos-browser.json` (66 eventos, sessão
 * WebRTC real em Chrome, microfone falso, corpo de sessão montado pelo NOSSO
 * backend, instruções da [T048] ativas).
 *
 * ## ⚠️ Por que o suspeito 1 enganou três documentos deste repo
 *
 * `-protocolo.js`, `-remissoes.js` e o `CLAUDE.md` do módulo diziam, cada um à
 * sua maneira, que *"o modelo já conversa sozinho durante a espera (9/9,
 * G-13 §1), então empurrar é opção, não necessidade"*. **As 9/9 são respostas
 * a interpelações.** O método do portão está escrito em letra: *"delegação
 * aberta, nenhuma resposta enviada, **e eu falando por cima**"*. Elas provam
 * que o modelo RESPONDE quando lhe falam na espera; nunca mediram que ele
 * COMEÇA um turno sozinho — e o consultor real pergunta e fica quieto, que é
 * precisamente o caso não medido.
 *
 * A instrução da [T048] diz *"continue conversando normalmente"*: continuar é
 * reativo. Quem interpela, aqui, somos nós — no lugar do consultor que
 * perguntou e está esperando.
 *
 * ## Que empurrar FUNCIONA é medido, e o `delegation_id` é a armadilha
 *
 * G-13 §4, sondagem C: `commentary.append` com **`delegation_id: null`** na
 * delegação pendente → `session.commentary.appended`, e o modelo falou
 * (RMS 741,40, `" Ainda está em andamento, sigo acompanhando aqui."`). Com o
 * `delegation_id` REAL, `Unknown client delegation.` em 6/6 e o canal MUDO
 * (RMS 0,08). A recusa do id é N19 e mora em `montarAppendDeFala`, que é por
 * onde este módulo passa — herdar a guarda é a razão de não montar a mensagem
 * à mão aqui.
 *
 * ## ★ D-13/N16 continua de pé: falar "durante" é dizer O QUE SE ESTÁ FAZENDO
 *
 * Nenhuma frase daqui carrega número, cliente, ativo, período ou data-base.
 * Elas são constantes CONGELADAS em `-remissoes.js`, sem nenhuma interpolação,
 * e passam pela MESMA guarda (`conferirQueAFalaRemete`) que trava o
 * `function_call_output` — no ponto único de saída do transporte. O valor de
 * negócio segue indo ao CARTÃO; a voz remete a ele quando ele chega.
 */
import { formatarParaFala } from './formatar-valor-para-voz'
import {
  EVENTO_APPEND_DE_COMENTARIO,
  montarAppendDeFala,
  MOTIVO_DELEGACAO_FALHOU,
} from './transporte-tempo-real-protocolo'
import {
  FALAS_DE_ESPERA,
  montarFalaComValor,
} from './transporte-tempo-real-remissoes'

/**
 * ★ Quanto tempo depois do início da consulta a voz dá o PRIMEIRO sinal de
 * vida. **8 s é medido, não escolhido** — e por dois lados:
 *
 * 1. é o instante mais CEDO em que se observou o modelo falar com a delegação
 *    pendente: as três sessões `responses` do G-13 §1 interpelaram em `+8,0 s`
 *    e **3/3 falaram**. O relógio delas é ancorado no
 *    `session.delegation.created`, que é o mesmo instante em que este começa;
 * 2. cai DEPOIS do anúncio espontâneo, sem pisar nele: na [T050] o
 *    `" Certo, já tô consultando…"` terminou 2,3 s após o
 *    `session.delegation.created` (14,83 s − 12,51 s). Sobram 5,7 s de folga.
 *
 * Empurrar antes disso falaria por cima do próprio anúncio do modelo — duas
 * vozes na mesma frase é pior que 8 s de espera.
 */
export const MS_ATE_O_PRIMEIRO_ACOMPANHAMENTO = 8000

/**
 * ★ O intervalo entre um acompanhamento e o seguinte.
 *
 * 13 s é o espaçamento MEDIDO do G-13 §1: as interpelações caíram em +8,0 s,
 * +21,7 s e +34,8 s (≈ 13 s de passo) e **9/9** produziram fala nas três
 * sessões `responses`. É a única cadência para a qual existe medição de que a
 * fala na espera sai — e de que uma não atropela a outra.
 */
export const MS_ENTRE_ACOMPANHAMENTOS = 13000

/**
 * O acompanhamento de UMA sessão. Instância por sessão, como o executor de
 * delegação e o controle da fala, e pelo mesmo motivo: o temporizador é
 * daquela espera, e herdá-lo faria a sessão seguinte anunciar uma consulta
 * que não existe.
 *
 * @param {Object} params
 * @param {(mensagem: Object) => boolean} params.enviar põe no data channel —
 *   o MESMO `enviar` do executor, que é o ponto único de saída e onde mora a
 *   guarda de D-13.
 * @param {(motivo: string, detalhe: string) => void} params.aoRecusar o canal
 *   nomeado de falha do executor (`marcarFalhaDaMedicao` + `emitir('erro')`).
 *
 * ## ⚠️ Não re-adicione um `aindaEsperando()` dentro do temporizador
 *
 * A primeira versão desta task tinha um: *"antes de falar, confira se a
 * consulta ainda está em voo"*. Parece prudência e **é código morto** — a
 * mutação provou. Removê-lo deixou a suíte inteira VERDE, porque **todo**
 * caminho que zera o `_emVoo` do executor chama `parar()` no MESMO tique
 * (`_concluir` e `abortar`), e em JavaScript um callback de `setTimeout` não
 * interleava com o microtask que o cancelou. Não existe janela para a guarda
 * pegar.
 *
 * O preço de mantê-la não era zero: com ela no caminho, tirar o `parar()` do
 * `_concluir` — que é o mecanismo de VERDADE — deixava a suíte verde do mesmo
 * jeito. Ou seja, a guarda morta MASCARAVA a regressão do mecanismo vivo. É o
 * inverso do que uma guarda deve fazer.
 */
export const criarAcompanhamentoDaEspera = ({
  enviar,
  aoRecusar,
  valorEmMaos,
}) => {
  let _temporizador = null
  let _jaFalados = 0
  let _valorJaFalado = false

  const parar = () => {
    if (_temporizador) {
      clearTimeout(_temporizador)
      _temporizador = null
    }
    _jaFalados = 0
    // A consulta seguinte tem um valor seu; o "já falei o número" é da espera
    // que acabou, não da sessão.
    _valorJaFalado = false
  }

  /**
   * A frase da vez. A última da lista se REPETE: passado o terceiro aviso, a
   * espera é longa de fato, e "está demorando mais que o normal" continua
   * sendo verdade. Parar de falar ali devolveria exatamente o silêncio que
   * esta task existe para consertar.
   */
  const _falaDaVez = () =>
    FALAS_DE_ESPERA[Math.min(_jaFalados, FALAS_DE_ESPERA.length - 1)]

  const _marcarProximo = (emMs) => {
    _temporizador = setTimeout(() => {
      _temporizador = null
      _falar()
    }, emMs)
  }

  /**
   * ★ [correção do owner, 17/09/2026] **A voz adianta o número quando ele JÁ
   * ESTÁ EM MÃOS.** *"Pode até adiantar número se já tiver isso (ou seja, se a
   * resposta já estiver nas mãos do agente)."*
   *
   * O instante existe de verdade: o valor que `lerDesfechoDoTurnoFalado` lê
   * vem do CARTÃO, e o cartão entra no store quando a tool dele fecha — antes
   * do fim do turno. Havia, portanto, uma janela em que o número já estava na
   * tela do consultor e a voz ainda dizia "estou consultando".
   *
   * `null` quando não há valor legível — e aí a voz diz o ESTADO, como antes.
   * Nada de estimar nem antecipar: sem valor em mãos, não há número a falar.
   *
   * Uma vez só: repetir o mesmo número a cada 13 s seria a voz travada num
   * fato que já foi dito. Depois de anunciá-lo, o acompanhamento volta às
   * frases de estado, porque o TURNO continua.
   */
  const _falaComValorEmMaos = () => {
    if (_valorJaFalado || typeof valorEmMaos !== 'function') return null
    const emMaos = valorEmMaos()
    if (!emMaos) return null
    const falado = formatarParaFala(emMaos.valor, { tipo: emMaos.tipo })
    if (!falado) return null
    return {
      conteudo: montarFalaComValor({ valorFalado: falado }),
      // ★ D-O-3: a ORIGEM viaja junto até o ponto único de saída, e é contra
      // ela que a guarda refaz o arredondamento. Um número sem origem contra
      // o que conferir é precisamente o número que veio de lugar nenhum.
      origem: emMaos,
    }
  }

  const _falar = () => {
    const comValor = _falaComValorEmMaos()
    try {
      enviar(
        montarAppendDeFala({
          tipo: EVENTO_APPEND_DE_COMENTARIO,
          conteudo: comValor ? comValor.conteudo : _falaDaVez(),
        }),
        comValor ? comValor.origem : undefined
      )
      if (comValor) _valorJaFalado = true
    } catch (erro) {
      // A guarda de D-13 recusou a fala ANTES de ela ir ao fio: o dado não
      // vazou — `conferirQueAFalaRemete` lança antes do `_cano.enviar`. O que
      // não pode acontecer é a exceção subir de dentro de um `setTimeout`
      // (ninguém a pega) nem se repetir a cada 13 s. Registra-se a recusa
      // pelo canal nomeado e o acompanhamento para.
      if (typeof aoRecusar === 'function') {
        aoRecusar(
          (erro && erro.motivo) || MOTIVO_DELEGACAO_FALHOU,
          (erro && erro.message) || ''
        )
      }
      parar()
      return
    }
    // Só a fala de ESTADO avança a lista: o anúncio do valor não é uma das
    // três, e contá-lo faria a espera pular direto para "está demorando".
    if (!comValor) _jaFalados += 1
    _marcarProximo(MS_ENTRE_ACOMPANHAMENTOS)
  }

  /**
   * A consulta começou e o consultor passou a esperar. Rearma do zero — uma
   * segunda demanda na mesma sessão recomeça a contagem das frases, porque
   * ela é uma espera nova.
   */
  const comecar = () => {
    parar()
    _marcarProximo(MS_ATE_O_PRIMEIRO_ACOMPANHAMENTO)
  }

  /** Só diagnóstico — nunca para decidir comportamento. */
  const jaFalados = () => _jaFalados

  return { comecar, parar, jaFalados }
}
